import React, { createContext, useState, useEffect } from "react";
import { generateConversationID, generateMessageID } from "../utils/uuid";

export const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isCheckingUser, setIsCheckingUser] = useState(true);

  // console.log("Current User in ChatContext:", currentUser);

  // Check for existing user on mount
  useEffect(() => {
    const checkExistingUser = () => {
      const userId = localStorage.getItem("bsa_user_id");
      const userData = localStorage.getItem("bsa_user_data");

      if (userId && userData) {
        try {
          const parsedUserData = JSON.parse(userData);
          setCurrentUser(parsedUserData);
        } catch (error) {
          console.error("Error parsing user data:", error);
          // Clear invalid data
          localStorage.removeItem("bsa_user_id");
          localStorage.removeItem("bsa_user_data");
        }
      }

      setIsCheckingUser(false);
    };

    checkExistingUser();
  }, []);

  // Fetch conversations from API when user is loaded
  useEffect(() => {
    if (currentUser && currentUser.userId) {
      fetchConversations();
    }
  }, [currentUser]);

  const fetchConversations = async () => {
    if (!currentUser || !currentUser.userId) {
      console.log("No user found, skipping conversation fetch");
      return;
    }

    // console.log("Fetching conversations for user:", currentUser.userId);

    setConversationsLoading(true);

    try {
      const apiUrl = process.env.REACT_APP_GET_USER_CONVERSATIONS_URL || "";

      const response = await fetch(`${apiUrl}?userId=${currentUser.userId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch conversations: ${response.status}`);
      }

      let data = await response.json();
      data = data.map((item) => item?.json);

      // Assuming the API returns an array of conversations
      // Format should match existing conversation structure
      const formattedConversations = data.conversations || data || [];

      // Sort by updatedAt (most recent first)
      const sortedConversations = formattedConversations.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );

      // console.log("Fetched conversations:", sortedConversations);

      setConversations(sortedConversations);

      // console.log(
      //   `Loaded ${sortedConversations.length} conversations from API`
      // );
    } catch (error) {
      console.error("Error fetching conversations:", error);
      // On error, conversations will be empty array
      setConversations([]);
    } finally {
      setConversationsLoading(false);
    }
  };

  const registerUser = (userData) => {
    setCurrentUser(userData);
  };

  const createNewConversation = () => {
    const newConversation = {
      id: generateConversationID(),
      title: "New Conversation",
      messages: [],
      userId: currentUser?.userId || "anonymous",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isNew: true, // Flag to indicate this hasn't been saved to API yet
    };

    // Add to local state immediately for responsive UI
    setConversations((prev) => [newConversation, ...prev]);
    setActiveConversation(newConversation);
    setMessages([]);

    return newConversation;
  };

  const saveConversationToAPI = async (conversation) => {
    if (!conversation || conversation.messages.length === 0) {
      console.log("Skipping save - no messages in conversation");
      return;
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const conversationsEndpoint =
        process.env.REACT_APP_CONVERSATIONS_ENDPOINT || "/api/conversations";

      const method = conversation.isNew ? "POST" : "PUT";
      const url = conversation.isNew
        ? `${apiUrl}${conversationsEndpoint}`
        : `${apiUrl}${conversationsEndpoint}/${conversation.id}`;

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...conversation,
          isNew: undefined, // Remove the isNew flag before sending
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save conversation: ${response.status}`);
      }

      const savedConversation = await response.json();

      // Update local state with saved conversation
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id === conversation.id) {
            return { ...savedConversation, isNew: false };
          }
          return conv;
        })
      );

      // console.log(`Conversation ${conversation.id} saved to API`);
      return savedConversation;
    } catch (error) {
      console.error("Error saving conversation to API:", error);
      throw error;
    }
  };

  const loadConversation = async (conversationId) => {
    const conversation = conversations.find((c) => c.id === conversationId);
    // console.log("Loading conversation:", conversationId, conversation);

    if (conversation) {
      setActiveConversation(conversation);
      setMessages(conversation.messages || []);
    } else {
      // If conversation not in local state, try to fetch from API
      try {
        const apiUrl = process.env.REACT_APP_GET_CONVERSATION_BY_ID_URL || "";

        const response = await fetch(`${apiUrl}/${conversationId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const fetchedConversation = await response.json();

          // Add to local state
          setConversations((prev) => {
            // Check if already exists
            const exists = prev.find((c) => c.id === fetchedConversation.id);
            if (exists) {
              return prev.map((c) =>
                c.id === fetchedConversation.id ? fetchedConversation : c
              );
            }
            return [fetchedConversation, ...prev];
          });

          setActiveConversation(fetchedConversation);
          setMessages(fetchedConversation.messages || []);
        } else {
          console.error(`Conversation ${conversationId} not found`);
        }
      } catch (error) {
        console.error("Error loading conversation:", error);
      }
    }
  };

  const updateConversationMessages = async (conversationId, newMessages) => {
    const updatedConversation = conversations.find(
      (c) => c.id === conversationId
    );

    if (!updatedConversation) {
      console.error("Conversation not found for update");
      return;
    }

    const updatedConv = {
      ...updatedConversation,
      messages: newMessages,
      updatedAt: new Date().toISOString(),
    };

    // Update title based on first user message if it's still "New Conversation"
    if (
      updatedConversation.title === "New Conversation" &&
      newMessages.length > 0
    ) {
      const firstUserMessage = newMessages.find((m) => m.role === "human");
      if (firstUserMessage) {
        updatedConv.title =
          firstUserMessage.content.substring(0, 50) +
          (firstUserMessage.content.length > 50 ? "..." : "");
      }
    }

    // Update local state immediately for responsive UI
    setConversations((prev) =>
      prev.map((conv) => (conv.id === conversationId ? updatedConv : conv))
    );

    // Save to API if conversation has messages
    if (newMessages.length > 0) {
      // await saveConversationToAPI(updatedConv);
    }
  };

  const sendMessage = async (prompt) => {
    if (!prompt.trim()) return;

    // Create new conversation if none is active
    let currentConversation = activeConversation;
    if (!currentConversation) {
      currentConversation = createNewConversation();
    }

    // Add user message
    const userMessage = {
      id: generateMessageID(),
      role: "human",
      content: prompt,
      timestamp: new Date().toISOString(),
      userId: currentUser?.userId || "anonymous",
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);

    // Update local state immediately (will save to API after)
    updateConversationMessages(currentConversation.id, updatedMessages);

    // Send to backend
    setLoading(true);

    try {
      // Use environment variables for API configuration
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || "";

      const response = await fetch(`${apiUrl}${apiEndpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          sessionId: currentConversation.id.toString(),
          userId: currentUser?.userId || "anonymous",
          userName: currentUser?.name || "Anonymous",
          userEmail: currentUser?.email || "",
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const data = await response.json();
      // console.log("API response data:", data);

      // Add AI response
      const aiMessage = {
        id: data.id.toString(),
        role: "ai",
        content: data.output || "I'm sorry, I couldn't process that request.",
        timestamp: new Date(data.date).toISOString(),
      };

      const finalMessages = [...updatedMessages, aiMessage];
      setMessages(finalMessages);

      // Update and save conversation with both messages
      await updateConversationMessages(currentConversation.id, finalMessages);
    } catch (error) {
      console.error("Error sending message:", error);

      // Add error message
      const errorMessage = {
        id: generateMessageID(),
        role: "ai",
        content:
          "I'm having trouble connecting to the server. Please try again later.",
        timestamp: new Date().toISOString(),
        error: true,
      };

      const finalMessages = [...updatedMessages, errorMessage];
      setMessages(finalMessages);

      // Save even error messages to maintain conversation history
      await updateConversationMessages(currentConversation.id, finalMessages);
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (conversationId) => {
    try {
      // Delete from API
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const conversationsEndpoint =
        process.env.REACT_APP_CONVERSATIONS_ENDPOINT || "/api/conversations";

      const response = await fetch(
        `${apiUrl}${conversationsEndpoint}/${conversationId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        console.error(
          `Failed to delete conversation from API: ${response.status}`
        );
        // Continue with local deletion even if API fails
      }

      // Remove from local state
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));

      if (activeConversation?.id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }

      // console.log(`Conversation ${conversationId} deleted`);
    } catch (error) {
      console.error("Error deleting conversation:", error);

      // Still remove from local state even if API call fails
      setConversations((prev) => prev.filter((c) => c.id !== conversationId));

      if (activeConversation?.id === conversationId) {
        setActiveConversation(null);
        setMessages([]);
      }
    }
  };

  const clearUserData = () => {
    localStorage.removeItem("bsa_user_id");
    localStorage.removeItem("bsa_user_data");
    // Note: We don't clear conversations from API, just local state
    setCurrentUser(null);
    setConversations([]);
    setActiveConversation(null);
    setMessages([]);
  };

  const refreshConversations = async () => {
    await fetchConversations();
  };

  return (
    <ChatContext.Provider
      value={{
        conversations,
        setConversations,
        activeConversation,
        setActiveConversation,
        messages,
        setMessages,
        sendMessage,
        loading,
        conversationsLoading,
        createNewConversation,
        loadConversation,
        deleteConversation,
        currentUser,
        setCurrentUser,
        registerUser,
        isCheckingUser,
        clearUserData,
        refreshConversations,
        fetchConversations,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
